// 📁 src/admin/AccountPage.jsx

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export default function AccountPage() {
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [accountRegistry, setAccountRegistry] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [billingCycleFilter, setBillingCycleFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
    setAccountRegistry(registry);

    const allLogs = [];
    Object.keys(registry).forEach((email) => {
      const log = JSON.parse(localStorage.getItem(`paymentAuditLog_${email}`) || '[]');
      log.forEach((entry) => {
        allLogs.push({ ...entry, email });
      });
    });
    setAuditLog(allLogs);
  }, []);

  const filteredData = auditLog.filter((entry) => {
    const account = accountRegistry[entry.email] || {};
    const matchesCompany = companyFilter ? account.companyName === companyFilter : true;
    const matchesStatus = statusFilter ? entry.paymentStatus === statusFilter : true;
    const matchesMethod = paymentMethodFilter ? entry.method === paymentMethodFilter : true;
    const matchesCurrency = currencyFilter ? entry.currency === currencyFilter : true;
    const matchesBillingCycle = billingCycleFilter
      ? entry.billingCycle === billingCycleFilter
      : true;

    const purchase = new Date(entry.purchaseDate);
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    const matchesFrom = from ? purchase >= from : true;
    const matchesTo = to ? purchase <= to : true;

    return (
      matchesCompany &&
      matchesStatus &&
      matchesMethod &&
      matchesCurrency &&
      matchesBillingCycle &&
      matchesFrom &&
      matchesTo
    );
  });

  const totalsByCurrency = {};
  filteredData.forEach((entry) => {
    const currency = entry.currency || 'BDT';
    const amount = Number(entry.amount || 0);
    totalsByCurrency[currency] = (totalsByCurrency[currency] || 0) + amount;
  });

  const uniqueCompanies = [
    ...new Set(Object.values(accountRegistry).map((acc) => acc.companyName))
  ];
  const uniqueMethods = [...new Set(auditLog.map((entry) => entry.method).filter(Boolean))];
  const uniqueCurrencies = [...new Set(auditLog.map((entry) => entry.currency).filter(Boolean))];
  const uniqueBillingCycles = [
    ...new Set(auditLog.map((entry) => entry.billingCycle).filter(Boolean))
  ];

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Print Report</title>');
    printWindow.document.write(
      '<style>body{font-family:sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:6px;text-align:left;font-size:12px;}tfoot td{font-weight:bold;}</style>'
    );
    printWindow.document.write('</head><body>');
    printWindow.document.write(printContents);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="p-6">
      {/* Print Report Button */}
      <div className="mb-4">
        <button className="bg-green-600 text-white px-4 py-2 rounded" onClick={handlePrint}>
          Print Report
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap justify-between gap-4 mb-6">
        <div className="flex gap-4">
          <div>
            <label className="text-sm text-gray-600">Company Name</label>
            <select
              className="border px-2 py-1 rounded"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <option value="">Select Company</option>
              {uniqueCompanies.map((name, idx) => (
                <option key={idx} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Status</label>
            <select
              className="border px-2 py-1 rounded"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Select Status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Payment Method</label>
            <select
              className="border px-2 py-1 rounded"
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
            >
              <option value="">Select Method</option>
              {uniqueMethods.map((method, idx) => (
                <option key={idx} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Currency</label>
            <select
              className="border px-2 py-1 rounded"
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
            >
              <option value="">Select Currency</option>
              {uniqueCurrencies.map((cur, idx) => (
                <option key={idx} value={cur}>
                  {cur}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Billing Cycle</label>
            <select
              className="border px-2 py-1 rounded"
              value={billingCycleFilter}
              onChange={(e) => setBillingCycleFilter(e.target.value)}
            >
              <option value="">Select Cycle</option>
              {uniqueBillingCycles.map((cycle, idx) => (
                <option key={idx} value={cycle}>
                  {cycle}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-4">
          <div>
            <label className="text-sm text-gray-600">From Date</label>
            <input
              type="date"
              className="border px-2 py-1 rounded"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm text-gray-600">To Date</label>
            <input
              type="date"
              className="border px-2 py-1 rounded"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Printable Table */}
      <div ref={printRef}>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 text-sm text-gray-700">
              <th className="border px-3 py-2 text-left">Company Name</th>
              <th className="border px-3 py-2 text-left">Module Name</th>
              <th className="border px-3 py-2 text-left">Status</th>
              <th className="border px-3 py-2 text-left">Payment Date</th>
              <th className="border px-3 py-2 text-left">Due Date</th>
              <th className="border px-3 py-2 text-left">Billing Cycle</th>
              <th className="border px-3 py-2 text-left">Payment Method</th>
              <th className="border px-3 py-2 text-left">Currency</th>
              <th className="border px-3 py-2 text-left">Price</th>
              <th className="border px-3 py-2 text-left">Invoice</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-4 text-gray-500 italic">
                  No records found.
                </td>
              </tr>
            ) : (
              filteredData.map((entry, index) => {
                const account = accountRegistry[entry.email] || {};
                const handleInvoicePreview = () => {
                  sessionStorage.setItem('invoicePreviewData', JSON.stringify(entry));
                  window.open('/invoice-preview', '_blank');
                };

                return (
                  <tr key={index} className="text-sm text-gray-800">
                    <td className="border px-3 py-2">{account.companyName}</td>
                    <td className="border px-3 py-2">{entry.moduleName}</td>
                    <td className="border px-3 py-2">{entry.paymentStatus}</td>
                    <td className="border px-3 py-2">{entry.purchaseDate}</td>
                    <td className="border px-3 py-2">{entry.expiryDate}</td>
                    <td className="border px-3 py-2">{entry.billingCycle}</td>
                    <td className="border px-3 py-2">{entry.method}</td>
                    <td className="border px-3 py-2">{entry.currency}</td>
                    <td className="border px-3 py-2">{entry.amount}</td>
                    <td className="border px-3 py-2">
                      <button
                        className="text-indigo-600 underline text-sm"
                        onClick={handleInvoicePreview}
                      >
                        Print Invoice
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {filteredData.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 text-sm font-semibold text-gray-700">
                <td colSpan="8" className="border px-3 py-2 text-right">
                  Totals:
                </td>
                <td colSpan="2" className="border px-3 py-2 space-y-1">
                  {Object.entries(totalsByCurrency).map(([cur, amt]) => (
                    <div key={cur}>
                      {cur}: {amt}
                    </div>
                  ))}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
